// Angular modules
import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule, SecurityContext } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// 3rd party modules
import * as moment from 'moment-timezone';
import { JwtModule } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { DeviceDetectorModule, DeviceDetectorService } from 'ngx-device-detector';
import {
    DxAccordionModule,
    DxAutocompleteModule,
    DxBoxModule,
    DxButtonModule,
    DxButtonGroupModule,
    DxContextMenuModule,
    DxDataGridModule,
    DxDrawerModule,
    DxDropDownBoxModule,
    DxDropDownButtonModule,
    DxListModule,
    DxLoadPanelModule,
    DxMenuModule,
    DxPopupModule,
    DxResponsiveBoxModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxTreeViewModule,
    DxLoadIndicatorModule,
    DxPopoverModule,
    DxSortableModule,
    DxCheckBoxModule,
    DxTabsModule,
    DxDateBoxModule,
    DxFormModule,
    DxTagBoxModule,
    DxNumberBoxModule,
    DxValidatorModule,
    DxTextAreaModule
} from 'devextreme-angular';

import dxLoadPanel from 'devextreme/ui/load_panel';
import dxPieChart from 'devextreme/viz/pie_chart';
import dxDropDownButton from 'devextreme/ui/drop_down_button';
import dxDateBox from 'devextreme/ui/date_box';
import dxTextBox from 'devextreme/ui/text_box';
import dxTextArea from 'devextreme/ui/text_area';
import dxDataGrid from 'devextreme/ui/data_grid';
import dxTreeList from 'devextreme/ui/tree_list';
import dxPopup from 'devextreme/ui/popup';
import dxButton from 'devextreme/ui/button';
import dxSelectBox from 'devextreme/ui/select_box';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { SpellCheckerModule, SpellCheckerService } from 'ngx-spellchecker';

// Modules & Components imports
import { CCCommonModule, CCDataGridFeaturesModule, CCLocalStorageService, Utils } from 'cc-common';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared-module/shared.module';
import { UserModule } from './user-module/user.module';
import { SearchModule } from './search/search.module';
import { DashboardModule } from './dashboard-module/dashboard.module';
import { WorkflowDashboardModule } from './mywork/mywork-dashboard.module';
import { SharedWidgetsModule } from './shared-widgets-module/shared-widgets.module';
import { ManagementCommonModule } from './management-common/management-common.module';
import { BoardApprovalModule } from './board-approval/board-approval.module';

// Services imports
import { HtmlEditorService } from './_services/shared-services/html-editor.service';
import { CredentialsInterceptor } from './_interceptors/credentials-interceptor';
import { MPErrorsHandler } from './_interceptors/mp-errors-handler';
import { UriCachingInterceptor } from './_interceptors/uri-caching-interceptor';
import { AppInfoService } from './_services/app-info.service';
import { AuthService } from './_services/auth.service';
import { ClientErrorService } from './_services/shared-services/client-error.service';
import { DeviceService } from './_services/shared-services/device.service';
import { FileHelperService } from './_services/shared-services/file-helper.service';
import { FileReaderService } from './_services/shared-services/file-reader.service';
import { MPCacheRegistrationService } from './_services/shared-services/mp-cache-registration-service';
import { ScreenService } from './_services/shared-services/screen.service';
import { ServerErrorService } from './_services/shared-services/server-error.service';
import { UserBackgroundService } from './_services/shared-services/user-background.service';
import { CommunityService } from './_services/data-services/community.service';
import { UserService } from './_services/data-services/user.service';
import { WidgetsFactoryService } from './_services/data-services/widgets-factory.service';
import { DashboardsFactoryService } from './_services/data-services/dashboards-factory.service';

// Constant imports
import { StorageKeyConstants } from './_constants/storage-key-constants';
import { Constants } from './_constants/constants';
import { StringFormatConstants } from './_constants/string-format-constants';

// Component imports
import { AppComponent } from './app.component';
import { BrandingImageComponent } from './shared-components/branding-image/branding-image.component';
import { CiraChatComponent } from './shared-components/cira-chat/cira-chat.component';
import { ContactUsComponent } from './shared-components/contact-us/contact-us.component';
import { HeaderComponent } from './shared-components/header/header.component';
import { HomeComponent } from './shared-components/home/home.component';
import { SideNavOuterToolbarComponent } from './shared-components/side-nav-outer-toolbar/side-nav-outer-toolbar.component';
import { UserPanelComponent } from './shared-components/user-panel/user-panel.component';
import { CommunityQuickSearchComponent } from './shared-components/community-quick-search/community-quick-search.component';
import { CurrentOwnerQuickSearchComponent } from './shared-components/current-owner-quick-search/current-owner-quick-search.component';
import { SideNavigationMenuComponent } from './shared-components/side-navigation-menu/side-navigation-menu.component';
import { NavigationQuickSearchComponent } from './shared-components/navigation-quick-search/navigation-quick-search.component';
import { SideNavigationSearchToolbarComponent } from './shared-components/side-navigation-search-toolbar/side-navigation-search-toolbar.component';
import { BookmarkManagerComponent } from './shared-components/bookmark-manager/bookmark-manager.component';
import { TopNavigationMenuComponent } from './shared-components/top-navigation-menu/top-navigation-menu.component';
import { PortalAnnouncementComponent } from './shared-components/portal-announcement/portal-announcement.component';
import { PortalAnnouncementEditorComponent } from './shared-components/portal-announcement/portal-announcement-editor/portal-announcement-editor.component';
import { ManageQaUsersComponent } from './shared-components/manage-qa-users/manage-qa-users.component';
import { UserPortfolioModule } from './user-portfolio-module/user-portfolio.module';
import { InternalUserContactUsComponent } from './shared-components/contact-us/internal-user-contact-us/internal-user-contact-us.component';
import { ExternalUserContactUsComponent } from './shared-components/contact-us/external-user-contact-us/external-user-contact-us.component';
import { TimeExpenseConfirmationComponent } from './time-expense-submission-history-confirmation/components/time-expense-confirmation/time-expense-confirmation.component';
import { TimeExpenseReportMasterDetailGridComponent } from './time-expense-submission-history-confirmation/components/time-expense-report-master-detail-grid/time-expense-report-master-detail-grid.component';
import { TimeExpenseSubmissionReportViewerComponent } from './time-expense-submission-history-confirmation/components/time-expense-submission-report-viewer/time-expense-submission-report-viewer.component';
import { WorkQueueActionsComponent } from './work-queue-actions-confirmation/work-queue-actions/work-queue-actions.component';
import { ContactPageComponent } from './shared-components/contact-us/contact-page/contact-page.component';

export function ccTokenGetter() {
    return localStorage.getItem(StorageKeyConstants.apiTokenName);
}

@NgModule({
    declarations: [
        AppComponent,
        SideNavOuterToolbarComponent,
        ContactUsComponent,
        HeaderComponent,
        CiraChatComponent,
        BrandingImageComponent,
        HomeComponent,
        UserPanelComponent,
        SideNavigationMenuComponent,
        NavigationQuickSearchComponent,
        SideNavigationSearchToolbarComponent,
        CurrentOwnerQuickSearchComponent,
        CommunityQuickSearchComponent,
        BookmarkManagerComponent,
        TopNavigationMenuComponent,
        PortalAnnouncementComponent,
        PortalAnnouncementEditorComponent,
        ManageQaUsersComponent,
        InternalUserContactUsComponent,
        ExternalUserContactUsComponent,
        TimeExpenseConfirmationComponent,
        TimeExpenseReportMasterDetailGridComponent,
        TimeExpenseSubmissionReportViewerComponent,
        WorkQueueActionsComponent,
        ContactPageComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        FormsModule,
        HttpClientModule,
        DeviceDetectorModule,
        DxBoxModule,
        DxButtonModule,
        DxResponsiveBoxModule,
        DxLoadPanelModule,
        DxScrollViewModule,
        DxDrawerModule,
        DxPopupModule,
        DxTreeViewModule,
        DxToolbarModule,
        DxAccordionModule,
        DxListModule,
        DxContextMenuModule,
        DxAutocompleteModule,
        DxButtonGroupModule,
        DxSelectBoxModule,
        DxTextBoxModule,
        DxDropDownButtonModule,
        DxMenuModule,
        DxDropDownBoxModule,
        DxDataGridModule,
        DxLoadIndicatorModule,
        DxPopoverModule,
        DxSortableModule,
        DxCheckBoxModule,
        DxTabsModule,
        DxDateBoxModule,
        DxFormModule,
        DxTagBoxModule,
        DxTextAreaModule,
        DxNumberBoxModule,
        DxValidatorModule,
        EditorModule,
        SpellCheckerModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: ccTokenGetter,
                whitelistedDomains: [
                    'localhost:8080',
                    'localapi.ciranet.com',
                    'sandboxapi.ciranet.com',
                    'qaapi.ciranet.com',
                    'api.ciranet.com',
                    'devapi.ciranet.com',
                    'pocapi.ciranet.com'
                ]
            }
        }),
        CCCommonModule,
        SharedModule,
        SearchModule,
        DashboardModule.forFeature(
            StorageKeyConstants.userDashboardsKey,
            WidgetsFactoryService,
            DashboardsFactoryService
        ),
        AppRoutingModule,
        UserModule,
        WorkflowDashboardModule,
        UserPortfolioModule,
        SharedWidgetsModule,
        BoardApprovalModule,
        CCDataGridFeaturesModule,
        ManagementCommonModule
    ],
    providers: [
        CookieService,
        AuthService,
        ClientErrorService,
        DeviceDetectorService,
        DeviceService,
        MPCacheRegistrationService,
        ServerErrorService,
        ScreenService,
        AppInfoService,
        UserBackgroundService,
        FileReaderService,
        FileHelperService,
        CommunityService,
        UserService,
        CCLocalStorageService,
        SpellCheckerService,
        HtmlEditorService,
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        { provide: HTTP_INTERCEPTORS, useClass: UriCachingInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true },
        { provide: ErrorHandler, useClass: MPErrorsHandler }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
    constructor(private readonly sanitizer: DomSanitizer) {
        dxLoadPanel.defaultOptions({
            options: {
                delay: Constants.defaultLoadPanelDelay,
                closeOnOutsideClick: false,
                showIndicator: true
            }
        });
        dxPieChart.defaultOptions({
            options: {
                loadingIndicator: { text: '' }
            }
        });
        dxDropDownButton.defaultOptions({
            options: {
                dropDownOptions: { width: 'auto' }
            }
        });
        dxDateBox.defaultOptions({
            options: {
                min: null,
                placeholder: StringFormatConstants.dateFormat.toUpperCase(),
                useMaskBehavior: true,
                displayFormat: {
                    parser: (value: string) => {
                        const parts = value.split('/').filter((d) => !!d);
                        if (3 === parts.length) {
                            const currentYear = new Date().getFullYear().toString().substring(0, 2);
                            return new Date(
                                parts[2].length < 3 ? Number(`${currentYear}${parts[2]}`) : Number(parts[2]),
                                Number(parts[0]) - 1,
                                Number(parts[1])
                            );
                        } else if (2 === parts.length) {
                            const currentYear = new Date().getFullYear().toString();
                            return new Date(Number(`${currentYear}`), Number(parts[0]) - 1, Number(parts[1]));
                        }
                        return null;
                    },
                    formatter: (value: Date) => {
                        return moment(value).format(StringFormatConstants.dateFormat.toUpperCase());
                    }
                },
                // Only set the min value when the type of the component is 'time'.
                onInitialized(e: any) {
                    const type = e.component.option('type');
                    if (Utils.equalsIgnoreCase(type, 'time')) {
                        // If the type is 'time', set the 'min' option to null (no minimum value)
                        e.component.option('min', null);
                    } else if (Utils.isNullOrEmpty(e.component.option('min'))) {
                        e.component.option('min', new Date('1900-01-01'));
                    }
                }
            }
        });
        dxTextBox.defaultOptions({
            options: {
                onFocusOut: this.textValueChanged.bind(this),
                onEnterKey: this.textValueChanged.bind(this),
                inputAttr: { autocomplete: 'off' }
            }
        });
        dxTextArea.defaultOptions({
            options: {
                onFocusOut: this.textValueChanged.bind(this),
                onEnterKey: this.textValueChanged.bind(this),
                inputAttr: { autocomplete: 'off' }
            }
        });
        dxDataGrid.defaultOptions({
            options: {
                useLegacyColumnButtonTemplate: true
            }
        });
        dxTreeList.defaultOptions({
            options: {
                useLegacyColumnButtonTemplate: true
            }
        });
        dxButton.defaultOptions({
            options: {
                onClick: this.onButtonClick.bind(this)
            }
        });
        dxSelectBox.defaultOptions({
            options: {
                onKeyUp: this.onKeyUp.bind(this)
            }
        });
        dxPopup.defaultOptions({
            options: {
                onShown: (e: any) => {
                    e.component.repaint();
                },
                onShowing: (e: any) => {
                    const showTitle = e.component.option('showTitle');
                    let dragEnabled = e.component.option('dragEnabled');
                    if (showTitle && dragEnabled) {
                        const toolbarItems: any[] = e.component.option('toolbarItems');
                        toolbarItems.push({
                            name: 'pinItem',
                            toolbar: 'top',
                            location: 'after',
                            widget: 'dxButton',
                            options: {
                                stylingMode: 'text',
                                icon: 'fas fa-thumbtack fa-rotate-45',
                                onClick: (b) => {
                                    dragEnabled = e.component.option('dragEnabled');
                                    e.component.option('dragEnabled', !dragEnabled);
                                    b.component.option('icon', `fas fa-thumbtack ${dragEnabled ? '' : 'fa-rotate-45'}`);
                                }
                            }
                        });
                        e.component.option('toolbarItems', toolbarItems);
                    }
                },
                onHiding: (e: any) => {
                    const toolbarItems: any[] = e.component.option('toolbarItems');
                    const pinIndex: number = toolbarItems.findIndex((t) => t.name === 'pinItem');
                    if (pinIndex !== -1) {
                        toolbarItems.splice(pinIndex, 1);
                        e.component.option({ toolbarItems, dragEnabled: true });
                    }
                }
            }
        });
    }

    textValueChanged(event): void {
        const opt = event.component.option('elementAttr') || { sanitize: false };
        const value = event.component.option('value');
        if (typeof value === 'string') {
            let plainText = value;
            if (!!opt && !!opt.sanitize) {
                plainText = this.sanitizer.sanitize(SecurityContext.HTML, value);
                plainText = this.sanitizer.sanitize(SecurityContext.URL, plainText);
                plainText = this.getSafeText(plainText);
            }
            if (value.trim() !== value || plainText !== value) {
                event.component.option('value', plainText.trim());
            }
        }
    }

    onButtonClick(event): void {
        const classList = event.element.className.split(' ');
        const ccDummyButtonIndex = classList.findIndex((d) => d.toLowerCase() === 'cc-dummy-button'.toLowerCase());
        if (ccDummyButtonIndex === -1) {
            event.component.option('disabled', true);
            // ANG-2700: 100ms was not giving best result to avoid multiple API calls, like in case of Save button. Hence increased delay from 100ms to 1000ms
            setTimeout(() => {
                event.component.option('disabled', false);
            }, 1000);
        }
    }

    getSafeText(plain: string): string {
        return plain.replace(/&#10;/gi, '\r').replace(/&#34;/gi, '\n').replace('unsafe:', '');
    }

    onKeyUp(event: any): void {
        if (!!event && !!event.event && !!event.event.key) {
            if (event.event.key.toLowerCase() === 'tab') {
                if (!event.component.option('opened')) {
                    event.component.open();
                }
            }
        }
    }
}
